import {
  shortOrderRef,
  type MarketplaceFulfillmentStatus,
} from "@nexus/contracts";
import {
  ORDER_STATUSES,
  rollupOrderStatus,
  toOrderStatus,
  type OrderStatus,
} from "@nexus/order-status";

import { describeDeadline, localTodayYmd } from "@/features/marketplace/lib/order-deadline";
import {
  partnerFilledVideoSlots,
  scriptHasVideoNeedingPartnerRevision,
} from "@/features/marketplace/partner/lib/partner-videos";
import {
  orderLineMatchesTypeFilter,
  resolveOrderType,
  type OrderLineTypeFilterOption,
  type OrderType,
} from "@/features/marketplace/lib/order-line-kind";
import { scriptMatchesStatusFilter, statusFilterVideoView } from "@/features/marketplace/lib/status-filter-match";
import { describeOrderScript } from "@/features/marketplace/lib/order-script-display";
import {
  deadlineGroupKey,
  type DeadlineGroupKey,
} from "@/features/marketplace/lib/orders-list-model";
import type {
  OrderLineVideo,
  PartnerOrder,
  PartnerOrdersFilter,
  OrderScriptRow,
} from "@/features/marketplace/types/marketplace.types";

export const PARTNER_QUEUE_TABS = [
  "needs-action",
  "active",
  "awaiting-review",
  "done",
  "archive",
] as const;
export type PartnerQueueTab = (typeof PARTNER_QUEUE_TABS)[number];

export const PARTNER_PERSISTED_STATUS_FILTERS = ORDER_STATUSES.filter(
  (status) => status !== "IN_PRODUCTION",
);

const PARTNER_FILTER_FULFILLMENT_STATUS = {
  SENT_TO_PRODUCTION: "SENT_TO_PRODUCTION",
  SUBMITTED_FOR_FEEDBACK: "SUBMITTED_FOR_FEEDBACK",
  REVISION_REQUESTED: "SENT_FOR_REVISION",
  SUBMITTED_FOR_FINAL_APPROVAL: "SUBMITTED_FOR_FINAL_APPROVAL",
  DELIVERED: "ORDER_DELIVERED",
  CANCELLED: "CANCELLED",
} as const satisfies Record<
  Exclude<OrderStatus, "IN_PRODUCTION">,
  MarketplaceFulfillmentStatus
>;

export function toPartnerFilterFulfillmentStatus(
  status: OrderStatus,
): MarketplaceFulfillmentStatus {
  if (status === "IN_PRODUCTION") return "SENT_TO_PRODUCTION";
  return PARTNER_FILTER_FULFILLMENT_STATUS[status];
}

export const PARTNER_QUEUE_GROUP_BY_OPTIONS = [
  "none",
  "client",
  "deadline",
] as const;
export type PartnerQueueGroupBy = (typeof PARTNER_QUEUE_GROUP_BY_OPTIONS)[number];

export const PARTNER_QUEUE_SORT_BY_OPTIONS = [
  "deadline",
  "received",
  "updated",
  "client",
  "status",
  "scripts",
] as const;
export type PartnerQueueSortBy = (typeof PARTNER_QUEUE_SORT_BY_OPTIONS)[number];
export type PartnerQueueSortDirection = "asc" | "desc";

export type PartnerQueueDensity = "compact" | "comfortable";

export interface PartnerQueueFilters {
  tab: PartnerQueueTab;
  search: string;
  /** Empty means every client. */
  clientIds: string[];
  /** Empty means every status. */
  statuses: OrderStatus[];
  overdueOnly: boolean;
  dueFrom: string;
  dueTo: string;
  lineType: OrderLineTypeFilterOption;
  groupBy: PartnerQueueGroupBy;
  sortBy: PartnerQueueSortBy;
  sortDirection: PartnerQueueSortDirection;
  density: PartnerQueueDensity;
}

export const DEFAULT_PARTNER_QUEUE_FILTERS: PartnerQueueFilters = {
  tab: "needs-action",
  search: "",
  clientIds: [],
  statuses: [],
  overdueOnly: false,
  dueFrom: "",
  dueTo: "",
  lineType: "all",
  groupBy: "none",
  sortBy: "deadline",
  sortDirection: "asc",
  density: "compact",
};

export function partnerOrdersFilterForQueueTab(
  tab: PartnerQueueTab,
): PartnerOrdersFilter {
  switch (tab) {
    case "done":
      return "completed";
    case "archive":
      return "archive";
    case "needs-action":
    case "active":
    case "awaiting-review":
      return "active";
  }
}

export interface PartnerWorkItem {
  order: PartnerOrder;
  script: OrderScriptRow;
  title: string;
  subtitle: string | null;
  status: OrderStatus;
  dueDate: string | null;
  lastUpdatedAt: string;
  isOverdue: boolean;
  /** The Status filter is on and matched this line. */
  matchesStatusFilter: boolean;
  /** Filled videos that matched the Status filter; null when no video matched or no filter is on. */
  matchedVideos: OrderLineVideo[] | null;
  /** Filled videos the Status filter hides under this line; 0 once revealed. */
  hiddenVideoCount: number;
}

export interface PartnerWorkGroup {
  order: PartnerOrder;
  /** Lines the Status filter hid in this order; empty without a filter or once revealed. */
  statusHiddenItems: PartnerWorkItem[];
  /** From every line of the order, not just the filtered `items`. */
  orderType: OrderType;
  orderRef: string;
  items: PartnerWorkItem[];
  dueStart: string | null;
  dueEnd: string | null;
  briefUrl: string | null;
}

export interface PartnerQueueSection {
  key: string;
  groups: PartnerWorkGroup[];
}

const DEADLINE_SECTION_ORDER: Record<DeadlineGroupKey, number> = {
  overdue: 0,
  today: 1,
  thisWeek: 2,
  later: 3,
  unscheduled: 4,
};

export function buildPartnerWorkQueue(
  orders: readonly PartnerOrder[],
  filters: PartnerQueueFilters,
  unnamedScriptLabel: string,
  now = new Date(),
): PartnerWorkGroup[] {
  const today = localTodayYmd(now);
  const normalizedSearch = filters.search.trim().toLocaleLowerCase();

  const candidates = orders
    .flatMap((order) =>
      order.scripts.map((script) => {
        const display = describeOrderScript(script, unnamedScriptLabel);
        const dueDate = script.expectedDeliveryDate ?? order.etaDate;
        const status = partnerQueueItemStatus(script);
        return {
          order,
          script,
          title: display.title,
          subtitle: display.subtitle,
          status,
          dueDate,
          lastUpdatedAt:
            script.fulfillmentStatusUpdatedAt ??
            script.createdAt ??
            order.sentAt ??
            order.createdAt,
          isOverdue:
            status !== "DELIVERED" &&
            status !== "CANCELLED" &&
            describeDeadline(dueDate, today, order.vendor.workingWeekdays)
              ?.tone === "overdue",
          matchesStatusFilter: false,
          matchedVideos: null,
          hiddenVideoCount: 0,
        } satisfies PartnerWorkItem;
      }),
    )
    .filter((item) => matchesQueueTab(item.status, filters.tab))
    .filter((item) => orderLineMatchesTypeFilter(item.script, filters.lineType))
    .filter(
      (item) =>
        filters.clientIds.length === 0 ||
        filters.clientIds.includes(item.order.tenantId),
    )
    .filter((item) => !filters.overdueOnly || item.isOverdue)
    .filter((item) => matchesDueRange(item.dueDate, filters.dueFrom, filters.dueTo))
    .filter((item) => matchesSearch(item, normalizedSearch));

  const filterStatuses = new Set(
    filters.statuses.map(toPartnerFilterFulfillmentStatus),
  );
  const isStatusFiltered = filters.statuses.length > 0;
  const items: PartnerWorkItem[] = [];
  const statusHiddenItems = new Map<string, PartnerWorkItem[]>();
  for (const item of candidates) {
    if (!isStatusFiltered) {
      items.push(item);
      continue;
    }
    const matches =
      filters.statuses.includes(item.status) ||
      scriptMatchesStatusFilter(item.script, filterStatuses);
    if (!matches) {
      const hidden = statusHiddenItems.get(item.order.id) ?? [];
      hidden.push(item);
      statusHiddenItems.set(item.order.id, hidden);
      continue;
    }
    const view = statusFilterVideoView(item.script, filterStatuses);
    items.push({
      ...item,
      matchesStatusFilter: true,
      matchedVideos: view.matchesOnVideos ? view.visibleVideos : null,
      hiddenVideoCount: view.hiddenVideoCount,
    });
  }

  const groups = new Map<string, PartnerWorkGroup>();
  for (const item of items) {
    const existing = groups.get(item.order.id);
    if (existing) {
      existing.items.push(item);
      continue;
    }

    const allOrderDisplays = item.order.scripts.map((script) =>
      describeOrderScript(script, unnamedScriptLabel),
    );
    const dueDates = item.order.scripts
      .map((script) => script.expectedDeliveryDate ?? item.order.etaDate)
      .filter((date): date is string => Boolean(date))
      .sort();
    groups.set(item.order.id, {
      order: item.order,
      statusHiddenItems: statusHiddenItems.get(item.order.id) ?? [],
      orderType: resolveOrderType(item.order.scripts),
      orderRef: shortOrderRef(item.order.id) ?? item.order.id.slice(-8),
      items: [item],
      dueStart: dueDates[0] ?? null,
      dueEnd: dueDates.at(-1) ?? null,
      briefUrl:
        allOrderDisplays.find((display) => display.briefUrl)?.briefUrl ?? null,
    });
  }

  return sortPartnerWorkGroups(
    [...groups.values()],
    filters.sortBy,
    filters.sortDirection,
  );
}

export function sectionPartnerWorkGroups(
  groups: readonly PartnerWorkGroup[],
  groupBy: PartnerQueueGroupBy,
  todayYmd: string,
): PartnerQueueSection[] {
  if (groupBy === "none") {
    return [{ key: "all", groups: [...groups] }];
  }

  const buckets = new Map<string, PartnerWorkGroup[]>();
  for (const group of groups) {
    const key =
      groupBy === "client"
        ? group.order.tenantId
        : deadlineGroupKey(
            describeDeadline(
              group.dueStart,
              todayYmd,
              group.order.vendor.workingWeekdays,
            )?.tone ?? null,
          );
    const bucket = buckets.get(key);
    if (bucket) bucket.push(group);
    else buckets.set(key, [group]);
  }

  const sections = [...buckets.entries()].map(([key, sectionGroups]) => ({
    key,
    groups: sectionGroups,
  }));
  if (groupBy === "deadline") {
    sections.sort(
      (a, b) =>
        DEADLINE_SECTION_ORDER[a.key as DeadlineGroupKey] -
        DEADLINE_SECTION_ORDER[b.key as DeadlineGroupKey],
    );
  }
  return sections;
}

export function countActivePartnerFilters(filters: PartnerQueueFilters): number {
  return (
    (filters.search.trim() ? 1 : 0) +
    (filters.clientIds.length > 0 ? 1 : 0) +
    (filters.statuses.length > 0 ? 1 : 0) +
    (filters.overdueOnly ? 1 : 0) +
    (filters.dueFrom || filters.dueTo ? 1 : 0) +
    (filters.lineType !== "all" ? 1 : 0)
  );
}

export function countPartnerOverdue(
  orders: readonly PartnerOrder[],
  filters: Omit<PartnerQueueFilters, "overdueOnly">,
  unnamedScriptLabel: string,
  now = new Date(),
): number {
  return buildPartnerWorkQueue(
    orders,
    { ...filters, overdueOnly: false },
    unnamedScriptLabel,
    now,
  ).reduce(
    (count, group) =>
      count + group.items.filter((item) => item.isOverdue).length,
    0,
  );
}

export function listPartnerClients(
  orders: readonly PartnerOrder[],
): Array<{ id: string; name: string }> {
  const clients = new Map<string, string>();
  for (const order of orders) {
    clients.set(order.tenantId, order.tenant.name);
  }
  return [...clients.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function isPartnerActionableStatus(status: OrderStatus): boolean {
  return (
    status === "SENT_TO_PRODUCTION" ||
    status === "IN_PRODUCTION" ||
    status === "REVISION_REQUESTED"
  );
}

/** Queue tab status: per-video revision counts as revision even when the line is mixed. */
export function partnerQueueItemStatus(
  script: Pick<OrderScriptRow, "fulfillmentStatus" | "videos">,
): OrderStatus {
  if (scriptHasVideoNeedingPartnerRevision(script)) {
    return "REVISION_REQUESTED";
  }
  return toOrderStatus(script.fulfillmentStatus);
}

/** Tab that shows a script when arriving from an email deep link. */
export function partnerQueueTabForStatus(status: OrderStatus): PartnerQueueTab {
  if (status === "DELIVERED") return "done";
  if (status === "CANCELLED") return "archive";
  if (
    status === "SUBMITTED_FOR_FEEDBACK" ||
    status === "SUBMITTED_FOR_FINAL_APPROVAL"
  ) {
    return "awaiting-review";
  }
  if (isPartnerActionableStatus(status)) return "needs-action";
  return "active";
}

/**
 * A deep-linked script opens on its own tab. Type resets only when it would hide the
 * line, because opening a row from the list also routes through the deep link.
 */
export function partnerQueueFiltersForDeepLink(
  filters: PartnerQueueFilters,
  script: Pick<OrderScriptRow, "fulfillmentStatus" | "briefId" | "customAssetKind" | "videos">,
): PartnerQueueFilters {
  return {
    ...filters,
    tab: partnerQueueTabForStatus(partnerQueueItemStatus(script)),
    lineType: orderLineMatchesTypeFilter(script, filters.lineType)
      ? filters.lineType
      : "all",
  };
}

export function clearPartnerQueueFilters(
  filters: PartnerQueueFilters,
): PartnerQueueFilters {
  return {
    ...filters,
    search: "",
    clientIds: [],
    statuses: [],
    overdueOnly: false,
    dueFrom: "",
    dueTo: "",
    lineType: "all",
  };
}

function matchesQueueTab(status: OrderStatus, tab: PartnerQueueTab): boolean {
  switch (tab) {
    case "needs-action":
      return isPartnerActionableStatus(status);
    case "active":
      return status !== "DELIVERED" && status !== "CANCELLED";
    case "awaiting-review":
      return (
        status === "SUBMITTED_FOR_FEEDBACK" ||
        status === "SUBMITTED_FOR_FINAL_APPROVAL"
      );
    case "done":
      return status === "DELIVERED";
    case "archive":
      return status === "CANCELLED";
  }
}

function matchesDueRange(
  dueDate: string | null,
  dueFrom: string,
  dueTo: string,
): boolean {
  if (!dueFrom && !dueTo) return true;
  if (!dueDate) return false;
  if (dueFrom && dueDate < dueFrom) return false;
  if (dueTo && dueDate > dueTo) return false;
  return true;
}

function matchesSearch(
  item: PartnerWorkItem,
  normalizedSearch: string,
): boolean {
  if (!normalizedSearch) return true;
  return [
    item.order.id,
    shortOrderRef(item.order.id),
    item.order.tenant.name,
    item.title,
    item.subtitle,
  ]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase()
    .includes(normalizedSearch);
}

function sortPartnerWorkGroups(
  groups: PartnerWorkGroup[],
  sortBy: PartnerQueueSortBy,
  direction: PartnerQueueSortDirection,
): PartnerWorkGroup[] {
  return [...groups].sort((a, b) => {
    const aItem = earliestItem(a);
    const bItem = earliestItem(b);
    let comparison: number;
    if (sortBy === "client") {
      comparison =
        a.order.tenant.name.localeCompare(b.order.tenant.name) ||
        compareByDeadline(aItem, bItem);
    } else if (sortBy === "updated") {
      comparison = aItem.lastUpdatedAt.localeCompare(bItem.lastUpdatedAt);
    } else if (sortBy === "received") {
      const aReceived = a.order.sentAt ?? a.order.createdAt;
      const bReceived = b.order.sentAt ?? b.order.createdAt;
      comparison = aReceived.localeCompare(bReceived);
    } else if (sortBy === "scripts") {
      comparison =
        a.items.length - b.items.length || compareByDeadline(aItem, bItem);
    } else if (sortBy === "status") {
      comparison =
        ORDER_STATUSES.indexOf(
          rollupOrderStatus(a.items.map((item) => item.status)),
        ) -
          ORDER_STATUSES.indexOf(
            rollupOrderStatus(b.items.map((item) => item.status)),
          ) || compareByDeadline(aItem, bItem);
    } else {
      comparison = compareByDeadline(aItem, bItem);
    }

    if (sortBy === "deadline") {
      if (aItem.dueDate === null && bItem.dueDate !== null) return 1;
      if (bItem.dueDate === null && aItem.dueDate !== null) return -1;
    }
    return comparison * (direction === "asc" ? 1 : -1);
  });
}

function earliestItem(group: PartnerWorkGroup): PartnerWorkItem {
  const ranked = [...group.items].sort(compareByDeadline);
  const first = ranked[0];
  if (!first) {
    throw new Error("Partner work group has no scripts");
  }
  return first;
}

function compareByDeadline(a: PartnerWorkItem, b: PartnerWorkItem): number {
  if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
  if (a.dueDate !== b.dueDate) {
    if (a.dueDate === null) return 1;
    if (b.dueDate === null) return -1;
    return a.dueDate.localeCompare(b.dueDate);
  }
  return (
    a.order.tenant.name.localeCompare(b.order.tenant.name) ||
    a.title.localeCompare(b.title)
  );
}

/**
 * Orders and lines a Status filter opens at render. Computed, never stored, so
 * clearing the filter returns to whatever the user had expanded.
 */
export function partnerStatusFilterExpansion(
  groups: readonly PartnerWorkGroup[],
  statusFilterActive: boolean,
): { orderIds: ReadonlySet<string>; scriptIds: ReadonlySet<string> } {
  if (!statusFilterActive) {
    return { orderIds: new Set(), scriptIds: new Set() };
  }
  return {
    orderIds: new Set(groups.map((group) => group.order.id)),
    scriptIds: new Set(
      groups.flatMap((group) => group.items.map((item) => item.script.id)),
    ),
  };
}

/** Filled video rows a line shows: only the matched ones while the filter hides others. */
export function partnerShownVideos(item: PartnerWorkItem): OrderLineVideo[] {
  if (item.hiddenVideoCount > 0 && item.matchedVideos) return item.matchedVideos;
  return partnerFilledVideoSlots(item.script);
}

/**
 * The group with every line and video the Status filter hid shown again, in
 * order line order. Matched rows keep their match so they stay highlighted.
 */
export function revealPartnerWorkGroup(group: PartnerWorkGroup): PartnerWorkGroup {
  const position = new Map(
    group.order.scripts.map((script, index) => [script.id, index]),
  );
  const items = [...group.items, ...group.statusHiddenItems]
    .map((item) => ({ ...item, hiddenVideoCount: 0 }))
    .sort(
      (left, right) =>
        (position.get(left.script.id) ?? 0) -
        (position.get(right.script.id) ?? 0),
    );
  return { ...group, items, statusHiddenItems: [] };
}
